/**
 * PageSection - Consistent section layout for CMS-style pages
 *
 * Provides uniform structure for entity management pages:
 * - Title with optional badge/count
 * - Icon with styled background
 * - Description text
 * - Action buttons
 * - Content area with configurable padding
 *
 * Variants:
 * - 'default': Standard section with subtle styling
 * - 'card': Elevated card with shadow and border
 * - 'flush': No padding, content fills container
 * - 'hero': Large featured section for page headers
 */

import { ReactNode, CSSProperties, useState } from 'react';
import { LucideIcon } from 'lucide-react';

export interface PageSectionProps {
  // Header
  title?: string;
  /** @deprecated Icon is no longer rendered (post-SKOOL-33). Pass for API compat only. */
  icon?: LucideIcon;
  /** @deprecated iconColor is no longer rendered (post-SKOOL-33). Pass for API compat only. */
  iconColor?: string;
  count?: number;
  description?: string;

  // Actions
  actions?: ReactNode;

  // Content
  children: ReactNode;

  // Styling
  variant?: 'default' | 'card' | 'flush' | 'hero';
  padding?: 'none' | 'sm' | 'md' | 'lg';
  className?: string;
  style?: CSSProperties;

  // Behavior
  collapsible?: boolean;
  defaultCollapsed?: boolean;
}

const PADDING_SIZES = {
  none: 0,
  sm: 12,
  md: 20,
  lg: 28,
};

const VARIANT_STYLES: Record<
  string,
  {
    background: string;
    borderRadius: string;
    border: string;
    shadow: string;
    headerBorder?: string;
    headerBackground?: string;
    color?: string;
  }
> = {
  default: {
    background: 'transparent',
    borderRadius: '0',
    border: 'none',
    shadow: 'none',
  },
  card: {
    background: '#ffffff',
    borderRadius: 'var(--radius-lg, 8px)',
    border: '1px solid var(--gray-200, #e5e7eb)',
    shadow: 'var(--shadow-md, 0 4px 8px rgba(0,0,0,0.06))',
  },
  flush: {
    background: 'transparent',
    borderRadius: '0',
    border: 'none',
    shadow: 'none',
  },
  hero: {
    background: 'var(--bg-sidebar)',
    borderRadius: 'var(--radius-xl, 12px) var(--radius-xl, 12px) 0 0',
    border: 'none',
    shadow: 'none',
    color: '#ffffff',
  },
};

export function PageSection({
  title,
  count,
  description,
  actions,
  children,
  variant = 'default',
  padding = 'md',
  className,
  style,
  collapsible = false,
  defaultCollapsed = false,
}: PageSectionProps) {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const hasHeader = title || description || actions;
  const paddingValue = PADDING_SIZES[padding];
  const variantStyle = VARIANT_STYLES[variant] || VARIANT_STYLES.default;

  const wrapperStyles: CSSProperties = {
    background: variantStyle.background,
    borderRadius: variantStyle.borderRadius,
    border: variantStyle.border,
    boxShadow: variantStyle.shadow,
    overflow: 'hidden',
    transition: 'box-shadow 0.2s ease',
    ...style,
  };

  const headerStyles: CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '16px',
    padding: variant === 'hero' ? `${paddingValue + 8}px ${paddingValue}px` : 0,
    paddingBottom: variant === 'hero' ? `${paddingValue + 8}px` : 16,
    borderBottom: variant === 'hero' ? '4px solid var(--gold-500)' : undefined,
    cursor: collapsible ? 'pointer' : undefined,
  };

  const contentStyles: CSSProperties = {
    padding: variant === 'flush' ? 0 : paddingValue,
    display: isCollapsed ? 'none' : 'block',
  };

  return (
    <div className={className} style={wrapperStyles}>
      {hasHeader && (
        <div
          style={headerStyles}
          onClick={collapsible ? () => setIsCollapsed(!isCollapsed) : undefined}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            {title && (
              <div className="page-section-header" style={{ marginBottom: 0 }}>
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}
                >
                  <h2
                    className="page-section-title"
                    style={variant === 'hero' ? { color: '#ffffff', fontSize: '28px' } : undefined}
                  >
                    {title}
                  </h2>
                  {count !== undefined && (
                    <span
                      style={{
                        padding: '3px 10px',
                        fontSize: '12px',
                        fontWeight: 600,
                        color: variant === 'hero' ? '#ffffff' : 'var(--gray-600)',
                        background:
                          variant === 'hero' ? 'rgba(255,255,255,0.15)' : 'var(--gray-100)',
                        borderRadius: '20px',
                        letterSpacing: '0.02em',
                      }}
                    >
                      {count.toLocaleString()}
                    </span>
                  )}
                  {collapsible && (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{
                        color: variant === 'hero' ? 'rgba(255,255,255,0.7)' : 'var(--gray-400)',
                        transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease',
                      }}
                    >
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  )}
                </div>
                {description && (
                  <p
                    className="page-section-description"
                    style={variant === 'hero' ? { color: 'rgba(255, 255, 255, 0.7)' } : undefined}
                  >
                    {description}
                  </p>
                )}
                {variant !== 'hero' && <div className="page-section-rule" aria-hidden="true" />}
              </div>
            )}
            {!title && description && <p className="page-section-description">{description}</p>}
          </div>

          {actions && (
            <div style={{ display: 'flex', gap: '8px', flexShrink: 0, alignItems: 'center' }}>
              {actions}
            </div>
          )}
        </div>
      )}

      <div style={contentStyles}>{children}</div>
    </div>
  );
}

/**
 * PageHeader - Top-level page header with title, description, and actions
 */
export interface PageHeaderProps {
  title: string;
  subtitle?: string;
  description?: string;
  actions?: ReactNode;
  breadcrumb?: ReactNode;
  /** @deprecated Icon is no longer rendered (post-SKOOL-33). Pass for API compat only. */
  icon?: LucideIcon;
  /** @deprecated iconColor is no longer rendered (post-SKOOL-33). Pass for API compat only. */
  iconColor?: string;
}

/**
 * PageHeader — Top-level page header (caps eyebrow + bold title + 32px gold rule).
 * `subtitle` becomes the eyebrow caps label; defaults to 'OVERVIEW' when omitted.
 * `icon` is preserved for API compatibility but no longer rendered (the ESPN
 * visual doesn't use rounded-icon-rect headers).
 */
export function PageHeader({ title, subtitle, description, actions, breadcrumb }: PageHeaderProps) {
  const eyebrow = (subtitle ?? 'OVERVIEW').toUpperCase();

  return (
    <div style={{ marginBottom: 24 }}>
      {breadcrumb && (
        <div
          style={{
            marginBottom: 8,
            fontSize: 13,
            color: 'var(--gray-500)',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          {breadcrumb}
        </div>
      )}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 16,
        }}
      >
        <header className="page-section-header" style={{ marginBottom: 0, flex: 1 }}>
          <p className="page-section-label">{eyebrow}</p>
          <h1 className="page-section-title">{title}</h1>
          {description && <p className="page-section-description">{description}</p>}
          <div className="page-section-rule" aria-hidden="true" />
        </header>
        {actions && (
          <div style={{ display: 'flex', gap: 8, flexShrink: 0, alignItems: 'center' }}>
            {actions}
          </div>
        )}
      </div>
    </div>
  );
}

export default PageSection;
